/**
 * Build nested tree structure from flat file paths
 */

import type { TreeNode } from './types.js';

/**
 * Item with a path - more flexible than requiring full FileInfo
 */
export interface PathItem {
  path: string;
}

/**
 * Build a nested tree structure from flat file paths.
 * Folders are virtual - they exist only because files have paths that include them.
 */
export function buildFileTree(files: PathItem[], expandedPaths: Set<string>): TreeNode[] {
  const root: TreeNode[] = [];
  const folderMap = new Map<string, TreeNode>();

  // Sort files by path for consistent ordering
  const sortedFiles = [...files].sort((a, b) => a.path.localeCompare(b.path));

  for (const file of sortedFiles) {
    const pathParts = file.path.split('/');
    const fileName = pathParts.pop()!;

    // Ensure all parent folders exist
    let currentPath = '';
    let currentChildren = root;

    for (let i = 0; i < pathParts.length; i++) {
      const folderName = pathParts[i];
      currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;

      let folder = folderMap.get(currentPath);
      if (!folder) {
        folder = {
          id: currentPath,
          name: folderName,
          type: 'folder',
          path: currentPath,
          depth: i,
          children: [],
          isExpanded: expandedPaths.has(currentPath),
        };
        folderMap.set(currentPath, folder);
        currentChildren.push(folder);
        // Sort after adding (folders before files, then alphabetically)
        sortChildren(currentChildren);
      }
      currentChildren = folder.children!;
    }

    // Add the file
    const fileNode: TreeNode = {
      id: file.path,
      name: fileName.replace('.src.md', ''),
      type: 'file',
      path: file.path,
      depth: pathParts.length,
    };
    currentChildren.push(fileNode);
    sortChildren(currentChildren);
  }

  return root;
}

/**
 * README files (case-insensitive, with or without extension) act as the
 * project's home page and are pinned above everything else at the root.
 */
function isReadmeName(name: string): boolean {
  return /^readme$/i.test(name);
}

/**
 * Sort children: README first (root only), then folders, then alphabetically by name.
 */
function sortChildren(children: TreeNode[]): void {
  children.sort((a, b) => {
    // README at the project root pinned to top
    const aReadme = a.depth === 0 && a.type === 'file' && isReadmeName(a.name);
    const bReadme = b.depth === 0 && b.type === 'file' && isReadmeName(b.name);
    if (aReadme !== bReadme) return aReadme ? -1 : 1;

    // Folders before files
    if (a.type !== b.type) {
      return a.type === 'folder' ? -1 : 1;
    }
    // Then alphabetically
    return a.name.localeCompare(b.name);
  });
}

/**
 * Get all file paths under a folder (for moving/deleting folders)
 */
export function getFilesUnderFolder(nodes: TreeNode[], folderPath: string): string[] {
  const files: string[] = [];

  function traverse(nodeList: TreeNode[]) {
    for (const node of nodeList) {
      if (node.type === 'file' && node.path.startsWith(folderPath + '/')) {
        files.push(node.path);
      } else if (node.type === 'folder' && node.children) {
        traverse(node.children);
      }
    }
  }

  traverse(nodes);
  return files;
}

/**
 * Find a node by path in the tree
 */
export function findNode(nodes: TreeNode[], path: string): TreeNode | null {
  for (const node of nodes) {
    if (node.path === path) {
      return node;
    }
    if (node.type === 'folder' && node.children) {
      const found = findNode(node.children, path);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Get all folder paths that lead to a file (for auto-expanding)
 */
export function getParentPaths(filePath: string): string[] {
  const parts = filePath.split('/');
  parts.pop(); // Remove filename

  const paths: string[] = [];
  let current = '';

  for (const part of parts) {
    current = current ? `${current}/${part}` : part;
    paths.push(current);
  }

  return paths;
}

/**
 * Validate that a move operation is allowed
 * - Cannot move folder into itself or its descendants
 * - Cannot move to same location
 */
export function isValidMove(
  sourcePath: string,
  sourceType: 'file' | 'folder',
  targetFolderPath: string
): { valid: boolean; reason?: string } {
  // Same location check
  const sourceParent = sourcePath.includes('/')
    ? sourcePath.substring(0, sourcePath.lastIndexOf('/'))
    : '';

  if (sourceParent === targetFolderPath) {
    return { valid: false, reason: 'Already in this folder' };
  }

  // Folder into itself check
  if (sourceType === 'folder') {
    if (targetFolderPath === sourcePath || targetFolderPath.startsWith(sourcePath + '/')) {
      return { valid: false, reason: 'Cannot move folder into itself' };
    }
  }

  return { valid: true };
}

/**
 * Calculate the new path when moving a file or folder
 */
export function getNewPath(sourcePath: string, targetFolderPath: string): string {
  const name = sourcePath.split('/').pop()!;
  return targetFolderPath ? `${targetFolderPath}/${name}` : name;
}
